import {
  format,
  isAfter,
  isFuture,
  isToday,
  isValid,
  parse,
  isPast,
  subDays,
  addDays,
  getYear,
  isSameYear,
  isSameDay,
} from 'date-fns';

import { enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import { getCultureLocalizer } from '../shared/utils';
import { groupMatchesByDate } from '../mls-featured-match-v2/components/FeaturedMatchWrapper/utils';

const getDateOrDefault = (dateString) => {
  if (!dateString) {
    const now = Date.now();

    return { date: now, dateString: format(now, 'yyyy-MM-dd') };
  }

  return {
    date: parse(dateString, 'yyyy-MM-dd', new Date()),
    dateString,
  };
};

export const getLiteRanges = (matches = []) => {
  if (!matches || !matches?.length) {
    return null;
  }

  const todayMatches = matches.filter((match) =>
    isToday(dayjs(match?.matchDate).toDate())
  );
  const nextMatches = matches.filter((match) =>
    isFuture(dayjs(match?.matchDate).toDate())
  );
  const pastMatches = matches.filter((match) =>
    isPast(dayjs(match?.matchDate).toDate())
  );

  if (todayMatches?.length) {
    const now = format(Date.now(), 'yyyy-MM-dd');

    return {
      dateFromLite: now,
      dateToLite: now,
    };
  }

  if (nextMatches?.length) {
    const matchDayDate = format(
      dayjs(nextMatches[0].matchDate).toDate(),
      'yyyy-MM-dd'
    );
    return {
      dateFromLite: matchDayDate,
      dateToLite: matchDayDate,
    };
  }

  if (pastMatches?.length) {
    const matchDayDate = format(
      dayjs(pastMatches[0].matchDate).toDate(),
      'yyyy-MM-dd'
    );
    return {
      dateFromLite: matchDayDate,
      dateToLite: matchDayDate,
    };
  }

  return {
    dateFromLite: null,
    dateToLite: null,
  };
};

export const getMatchDayRange = (differenceDays = 1) => {
  const now = Date.now();
  const daysAgo = subDays(now, differenceDays);
  const daysLater = addDays(now, differenceDays);
  const from = {
    date: daysAgo,
    dateString: format(daysAgo, 'yyyy-MM-dd'),
  };
  const to = {
    date: daysLater,
    dateString: format(daysLater, 'yyyy-MM-dd'),
  };

  const formattedDateString =
    from.dateString === to.dateString
      ? format(from.date, 'PPPP', { locale: getCultureLocalizer() })
      : `${format(from.date, 'PPPP', {
          locale: getCultureLocalizer(),
        })} - ${format(to.date, 'PPPP', { locale: getCultureLocalizer() })}`;

  return { from: from.dateString, to: to.dateString, formattedDateString };
};

export const getMatchInitialDate = (matchDateTime, dateFrom, dateTo) => {
  const parsedMatchDateTime = matchDateTime
    ? new Date(Date.parse(matchDateTime))
    : null;
  const dateObj = parsedMatchDateTime
    ? {
        date: parsedMatchDateTime,
        dateString: format(parsedMatchDateTime, 'yyyy-MM-dd', {
          locale: getCultureLocalizer(),
        }),
      }
    : null;

  const from = dateObj ? dateObj : getDateOrDefault(dateFrom);
  const to = dateObj ? dateObj : getDateOrDefault(dateTo);
  const formattedDateString =
    from.dateString === to.dateString
      ? format(from.date, 'PPPP', { locale: getCultureLocalizer() })
      : `${format(from.date, 'PPPP', {
          locale: getCultureLocalizer(),
        })} - ${format(to.date, 'PPPP', { locale: getCultureLocalizer() })}`;

  return { from: from.dateString, to: to.dateString, formattedDateString };
};

export const getDateTranslation = (date, t, showDay = true) => {
  if (!date || !t) return null;

  const dayObj = new Date(date);
  const currentYear = getYear(new Date());
  const year = getYear(dayObj);
  const sameYear = currentYear == year;

  if (isValid(dayObj)) {
    const weekdayLabelFormat = format(dayObj, 'cccc', {
      locale: getCultureLocalizer(),
    })?.toLowerCase();
    const dayLabelFormat = format(dayObj, 'd');
    const monthLabelFormat = format(dayObj, 'MMMM', {
      locale: getCultureLocalizer(),
    })?.toLowerCase();

    if (getCultureLocalizer() !== enUS) {
      return `${showDay ? `${t(weekdayLabelFormat)} ` : ''}${dayLabelFormat} ${t(
        monthLabelFormat
      )} ${!sameYear ? year : ''}`.trim();
    }

    if (getCultureLocalizer() == enUS) {
      return `${showDay ? `${t(weekdayLabelFormat)} ` : ''}${t(
        monthLabelFormat
      )} ${dayLabelFormat} ${!sameYear ? year : ''}`.trim();
    }
  } else {
    return `${date} ${!sameYear ? year : ''}`;
  }
};

export function getNearestMatches(matches) {
  const groupedMatches = groupMatchesByDate(matches);
  let preMatches = null;

  if (groupedMatches) {
    preMatches = getTodaysNearestMatches(groupedMatches, addDays);

    if (!preMatches) {
      preMatches = getTodaysNearestMatches(groupedMatches, subDays);
    }
  }

  return preMatches;
}

function getTodaysNearestMatches(groupedMatches, getDate) {
  const now = new Date();
  const MAX_DIFFERENCE_DAYS = 60;
  let matches = null;

  for (let index = 0; index <= MAX_DIFFERENCE_DAYS; index++) {
    const date = getDate(now, index);
    const matchesDate = getMatchesFromDate(groupedMatches, date);

    if (matchesDate) {
      matches = matchesDate?.matches;
      break;
    }
  }

  return matches;
}

function getMatchesFromDate(groupedMatches, date) {
  return groupedMatches.find((currentGroupedMatches) => {
    if (isSameDay(currentGroupedMatches.date, date)) {
      return true;
    }
  });
}

export function getMatchDay(matches) {
  const matchDays = {};

  matches?.forEach((match) => {
    const matchDay = match?.match_day;
    matchDays[matchDay] = matchDays[matchDay] + 1 || 1;
  });

  const mostCommonMatchDay = Object.keys(matchDays)?.reduce(
    (a, b) => (matchDays[a] > matchDays[b] ? a : b),
    0
  );

  return mostCommonMatchDay;
}

export function getSportecClubId() {
  return window?.forgeVariables?.currentSite?.clubSportecId;
}
